import type { FusionDef, PassiveDef, ShapeName, WeaponDef } from '../core/types';
import type { Rng } from '../core/rng';
import { CONFIG } from '../config';
import type { WeaponInstance } from './weapons';
import type { OwnedPassive } from './stats';

export type ChoiceKind = 'weapon-new' | 'weapon-up' | 'passive-new' | 'passive-up' | 'heal' | 'fusion';

export interface LevelUpChoice {
  kind: ChoiceKind;
  id: string;
  name: string;
  /** 카드 본문 */
  desc: string;
  /** 획득 후 레벨. heal은 0 */
  level: number;
  maxLevel: number;
  color: string;
  shape: ShapeName;
  sprite?: string;
  /** 조합일 때 사라지는 재료 무기 id들 (고르기 전에는 추천 조합) */
  consumes?: string[];
  /** 1.25 조합일 때 재료로 고를 수 있는 무기 전부. 카드를 누르면 여기서 직접 고른다 */
  options?: FusionOption[];
  /** 조합일 때 골라야 하는 재료 수 (보통 3) */
  need?: number;
  /** 그중 조건(matches)을 만족해야 하는 최소 수 (보통 1) */
  needMatch?: number;
  /** 조합 조건을 사람 말로 — "황색" · "근접" · 없으면 빈 글 */
  reqLabel?: string;
  /** 진화 갈림길이면 고를 수 있는 갈래들. 카드를 누르면 이 중 하나를 고르는 화면이 뜬다. */
  branches?: Array<{ id: string; name: string; desc: string }>;
  /** 고른 갈래 id (갈림길 화면에서 채운다) */
  pick?: string;
}

/** 1.25 조합 재료 후보 하나 — "무엇을 합칠까" 화면에 뜨는 칸 */
export interface FusionOption {
  id: string;
  name: string;
  level: number;
  color: string;
  shape: ShapeName;
  sprite?: string;
  /** 이 무기가 조합 조건(속성·동작·지정 무기)을 만족하는가. 아니면 빈자리를 채우는 재료다 */
  matches: boolean;
}

/** 조합 조건을 카드·고르기 화면에 적을 말로. 조건이 없으면 빈 글 */
const ATTR_KO: Record<string, string> = {
  red: '적색', blue: '청색', grey: '회색', yellow: '황색', brown: '갈색', white: '백색',
};
const BEHAVIOR_KO: Record<string, string> = {
  melee: '근접', field: '장판', orbit: '궤도', projectile: '투사체', aura: '오라', nova: '폭발',
  chain: '연쇄', palm: '장풍', leap: '도약', dash: '대시', strike: '낙뢰', greatblade: '대검',
  ashblade: '잿빛대검', tempest: '회오리', blink: '순간이동',
};

function reqLabelOf(f: FusionDef): string {
  if (f.requires.attr) return ATTR_KO[f.requires.attr] ?? f.requires.attr;
  if (f.requires.behavior) return BEHAVIOR_KO[f.requires.behavior] ?? f.requires.behavior;
  return '';
}

/** 조합 재료가 될 수 있는 레벨인가 (FUSION_MIN_LEVEL 이상, 만렙이 그보다 낮으면 만렙) */
export function isFusionReady(w: WeaponInstance): boolean {
  return !w.isFusion && w.level >= Math.min(w.def.maxLevel, CONFIG.FUSION_MIN_LEVEL);
}

/**
 * 지금 조합할 수 있는 것들을 찾는다.
 * FUSION_MIN_LEVEL(6) 에 닿은 무기만 재료가 되고, 조합으로 나온 무기는 재료가 될 수 없다.
 */
export function availableFusions(
  fusions: readonly FusionDef[],
  ownedWeapons: readonly WeaponInstance[],
): Array<{ def: FusionDef; consumes: string[]; options: FusionOption[]; need: number; needMatch: number; reqLabel: string }> {
  const ready = ownedWeapons.filter(isFusionReady);
  const out: Array<{ def: FusionDef; consumes: string[]; options: FusionOption[]; need: number; needMatch: number; reqLabel: string }> = [];

  for (const f of fusions) {
    if (ownedWeapons.some((w) => w.def.id === f.id)) continue; // 이미 가지고 있다
    const need = f.requires.count ?? CONFIG.FUSION_INGREDIENTS;
    if (ready.length < need) continue;

    // 조건에 맞는 재료부터 고르고, 모자란 자리는 아무 재료 무기로 채운다
    let matching: WeaponInstance[];
    if (f.requires.ids) {
      const ids = f.requires.ids;
      matching = ready.filter((w) => ids.includes(w.def.id));
    } else if (f.requires.attr) {
      // fusionAs: 그 속성으로도 쳐 주는 무기 (1.23 비급 검 둘 — 복마검 ↔ 삼재검)
      const attr = f.requires.attr;
      matching = ready.filter((w) => w.def.attr === attr || w.def.fusionAs?.includes(attr));
    } else if (f.requires.behavior) {
      matching = ready.filter((w) => w.def.behavior === f.requires.behavior);
    } else {
      matching = ready;
    }
    const mustMatch = f.requires.match ?? need;
    if (matching.length < mustMatch) continue;

    const chosen = matching.slice(0, mustMatch);
    for (const w of ready) {
      if (chosen.length >= need) break;
      if (!chosen.includes(w)) chosen.push(w);
    }
    if (chosen.length < need) continue;
    // 1.25: 추천 조합(consumes)과 함께, 재료가 될 수 있는 무기 전부를 넘긴다 — 사람이 직접 고른다.
    // 조건에 맞는 것(matching)을 앞에 둬서 고르기 화면에도 그 순서로 뜨게 한다.
    const options: FusionOption[] = ready
      .map((w) => ({
        id: w.def.id,
        name: w.def.name,
        level: w.level,
        color: w.def.color,
        shape: w.def.shape,
        sprite: w.def.sprite,
        matches: matching.includes(w),
      }))
      .sort((a, b) => Number(b.matches) - Number(a.matches));
    out.push({
      def: f,
      consumes: chosen.map((w) => w.def.id),
      options,
      need,
      needMatch: mustMatch,
      reqLabel: reqLabelOf(f),
    });
  }
  return out;
}

/**
 * 레벨업 선택지를 count 장(기본 3, 준은 4 — trait.choices) 만든다.
 * 조합이 열려 있으면 그것부터 채우고(단, 한 장은 평범한 선택지로 남긴다),
 * 나머지를 무기·패시브로 채운다. 후보가 모자라면 회복 카드로 메운다.
 */
export function buildChoices(
  rng: Rng,
  allWeapons: readonly WeaponDef[],
  allPassives: readonly PassiveDef[],
  ownedWeapons: readonly WeaponInstance[],
  ownedPassives: readonly OwnedPassive[],
  fusions: readonly FusionDef[] = [],
  /** 현재 체력 비율. 1이면 치유 카드를 넣지 않는다. */
  hpRatio = 1,
  /** 지금 캐릭터 id — 다른 캐릭터 전용 무기(only)는 카드에 안 넣는다 */
  characterId = '',
  /** 카드 수 */
  count: number = CONFIG.LEVELUP_CHOICES,
): LevelUpChoice[] {
  const pool: LevelUpChoice[] = [];

  for (const w of ownedWeapons) {
    if (w.level >= w.def.maxLevel) continue;
    const br = w.nextBranch;
    pool.push({
      kind: 'weapon-up',
      id: w.def.id,
      name: w.def.name,
      desc: br
        ? `진화 갈림길 — ${br.map((b) => `「${b.name}」`).join(' / ')} 중 하나를 고른다.`
        : w.nextDesc || w.def.description,
      level: w.level + 1,
      maxLevel: w.def.maxLevel,
      color: w.def.color,
      shape: w.def.shape,
      sprite: w.def.sprite,
      branches: br?.map((b) => ({ id: b.id, name: b.name, desc: b._desc })),
    });
  }

  if (ownedWeapons.length < CONFIG.MAX_WEAPONS) {
    for (const def of allWeapons) {
      if (ownedWeapons.some((w) => w.def.id === def.id)) continue;
      if (def.only && !def.only.includes(characterId)) continue;
      if (def.except?.includes(characterId)) continue;
      pool.push({
        kind: 'weapon-new',
        id: def.id,
        name: def.name,
        desc: def.description,
        level: 1,
        maxLevel: def.maxLevel,
        color: def.color,
        shape: def.shape,
        sprite: def.sprite,
      });
    }
  }

  for (const p of ownedPassives) {
    if (p.level >= p.def.maxLevel) continue;
    pool.push({
      kind: 'passive-up',
      id: p.def.id,
      name: p.def.name,
      desc: p.def.description,
      level: p.level + 1,
      maxLevel: p.def.maxLevel,
      color: p.def.color,
      shape: p.def.shape,
      sprite: p.def.sprite,
    });
  }

  if (ownedPassives.length < CONFIG.MAX_PASSIVES) {
    for (const def of allPassives) {
      if (ownedPassives.some((p) => p.def.id === def.id)) continue;
      pool.push({
        kind: 'passive-new',
        id: def.id,
        name: def.name,
        desc: def.description,
        level: 1,
        maxLevel: def.maxLevel,
        color: def.color,
        shape: def.shape,
        sprite: def.sprite,
      });
    }
  }

  // 치유는 "고를 게 없을 때 나오는 것"이 아니라 그냥 후보 중 하나다.
  // 체력이 깎였을수록 자주 뜨고, 꽉 찼으면 아예 안 뜬다.
  if (hpRatio < 0.98) {
    const copies = hpRatio < 0.5 ? 3 : hpRatio < 0.8 ? 2 : 1;
    for (let i = 0; i < copies; i++) pool.push(healChoice());
  }

  rng.shuffle(pool);

  // 맨손(무기 없이 시작한 준의 첫 레벨업)이면 카드는 전부 새 무기 — 싸울 수단부터
  if (ownedWeapons.length === 0) {
    const arms = pool.filter((c) => c.kind === 'weapon-new');
    if (arms.length > 0) return arms.slice(0, count);
  }

  // 조합은 드물게 열리는 기회라 뜨면 반드시 보여준다(단, 한 장은 평범한 선택지로 남긴다)
  const fused = availableFusions(fusions, ownedWeapons).map(({ def, consumes, options, need, needMatch, reqLabel }) => ({
    kind: 'fusion' as const,
    id: def.id,
    name: def.name,
    desc: `${def.description}\n\n▸ 가진 것 중에서 재료 ${need}개를 골라 합친다`,
    level: 1,
    maxLevel: def.maxLevel,
    color: def.color,
    shape: def.shape,
    sprite: def.sprite,
    consumes,
    options,
    need,
    needMatch,
    reqLabel,
  }));
  rng.shuffle(fused);

  const out = [...fused.slice(0, count - 1), ...pool].slice(0, count);

  while (out.length < count) out.push(healChoice());
  return out;
}

function healChoice(): LevelUpChoice {
  return {
    kind: 'heal',
    id: 'heal',
    name: '치유',
    desc: '최대 체력의 30%를 회복한다. 레벨업에서 고르면 레벨은 오르지 않는다.',
    level: 0,
    maxLevel: 0,
    color: '#3ddc84',
    shape: 'cross',
  };
}
